import React from 'react';
import { MapPin, Search, Clock, CheckCircle, Shield, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import StaticContentHelper from '../components/StaticContentHelper';
import Breadcrumbs from '../components/Breadcrumbs';

const SkipTracingPage = () => {
  const serviceData = [
    {
      name: 'Service of Process Skip Tracing',
      description: 'Locate defendants, witnesses, and parties for legal service',
    },
    {
      name: 'Legal Proceedings Location',
      description: 'Court-ordered location services for legal matters',
    },
    {
      name: 'Witness & Party Location',
      description: 'Locate witnesses and parties involved in legal cases',
    }
  ];

  const cities = [
    'Palm Springs', 'Riverside', 'Temecula', 'Palm Desert', 'Indio', 'Coachella',
    'La Quinta', 'Rancho Mirage', 'Cathedral City', 'Desert Hot Springs',
    'Indian Wells', 'Thousand Palms', 'Twentynine Palms', 'Yucca Valley',
    'Joshua Tree', 'Morongo Valley', 'Landers', 'Moreno Valley', 'Corona',
    'Menifee', 'Murrieta'
  ];

  const skipTracingSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Skip Tracing Services California",
    "description": "Professional skip tracing services for legal matters throughout California. Licensed professionals specializing in locating parties for service of process and court-related legal proceedings.",
    "provider": {
      "@type": "LegalService",
      "name": "Legal Document Specialist",
      "telephone": "(760) 866-9182",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "225 South Civic Drive, Ste 2-16",
        "addressLocality": "Palm Springs",
        "addressRegion": "CA",
        "postalCode": "92262",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "California"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "availableLanguage": "English",
      "servicePhone": "(760) 866-9182",
      "serviceUrl": `${window.location.origin}/skip-tracing`
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Service of Process Skip Tracing",
        "description": "Locate parties to facilitate service of process",
      },
      {
        "@type": "Offer",
        "name": "Legal Proceedings Location",
        "description": "Court-ordered location services for legal matters",
      }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is skip tracing legal in California?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, skip tracing is legal in California when performed by licensed private investigators, registered process servers, or licensed collection agencies who comply with federal and state regulations including the Fair Debt Collection Practices Act (FDCPA), Gramm-Leach-Bliley Act (GLBA), Telephone Consumer Protection Act (TCPA), and Fair Credit Reporting Act (FCRA). California law requires skip tracing to be performed by these licensed professionals to ensure adherence to legal standards and professional ethics."
        }
      },
      {
        "@type": "Question",
        "name": "How long does skip tracing take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most skip tracing investigations are completed within 24-72 hours for standard cases. However, turnaround times can vary based on the complexity of the case, availability of information, and whether the subject is actively avoiding contact. Complex cases may require additional time for thorough investigation and verification."
        }
      },
      {
        "@type": "Question",
        "name": "What information do you need to start a skip trace?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "To begin a skip trace, we typically need the subject's full name, last known address, date of birth (if available), Social Security number (if available), and any additional information such as previous addresses, known associates, employment history, or phone numbers. The more information provided, the faster and more accurate the investigation."
        }
      },
      {
        "@type": "Question",
        "name": "What types of skip tracing services do you provide?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We provide skip tracing services exclusively for legal matters. Our services are designed to facilitate service of process, locate witnesses for court proceedings, assist with heir and beneficiary searches in probate matters, and support other legitimate court-ordered legal needs. We serve attorneys, law firms, and legal professionals only, ensuring all investigations comply with California law and are conducted for authorized legal purposes."
        }
      },
      {
        "@type": "Question",
        "name": "Can you help with debt collection or personal searches?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, we only provide skip tracing services for legal matters. Our services are exclusively for attorneys, law firms, and legal professionals conducting authorized court-related proceedings. We specialize in facilitating service of process, locating witnesses, and supporting legitimate legal matters as required by California law. We do not provide skip tracing for debt collection, personal searches, or non-legal purposes."
        }
      },
      {
        "@type": "Question",
        "name": "Are skip tracing services confidential?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we maintain 100% confidentiality in all skip tracing investigations. All information is handled discreetly, securely, and in compliance with privacy laws. Our investigators undergo background checks and sign confidentiality agreements. We employ secure communication channels and encrypted data storage to protect sensitive client information and attorney-client privilege."
        }
      }
    ]
  };

  React.useEffect(() => {
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.textContent = JSON.stringify(skipTracingSchema);
    document.head.appendChild(serviceScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.textContent === JSON.stringify(skipTracingSchema) || 
            script.textContent === JSON.stringify(faqSchema)) {
          script.remove();
        }
      });
    };
  }, []);

  return (
    <div>
      <SEOHelmet 
        title="Skip Tracing California | Legal Service of Process Location | Professional Investigators"
        description="Professional skip tracing for legal matters in California. Licensed professionals specializing in service of process location and court-related proceedings. Request a quote online."
        keywords="skip tracing California, service of process location, legal skip tracing, locate defendant, witness location, court proceedings, process server skip trace"
        canonical="https://legaldocspecialist.com/skip-tracing"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              California Skip Tracing Services
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional skip tracing services for legal matters throughout California. Licensed professionals specializing in locating parties for service of process and court-related legal proceedings.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/quote?service=skip-tracing" className="btn-primary" data-testid="hero-quote-link">
                Get Skip Tracing Quote
              </Link>
              <a href="mailto:support@legaldocspecialist.com" className="btn-secondary">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Types */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Legal Skip Tracing Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Specialized location services exclusively for legal matters and court proceedings
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {[
              {
                name: 'Service of Process Location',
                icon: <Shield size={28} color="white" />,
                description: 'Locate defendants and parties to facilitate service of process for legal proceedings',
                features: ['Defendant location for service', 'Party identification and verification', 'Current address confirmation', 'Employment location research'],
              },
              {
                name: 'Witness Location Services',
                icon: <Search size={28} color="white" />,
                description: 'Locate witnesses essential to legal cases and court proceedings',
                features: ['Witness location and contact info', 'Subpoena service facilitation', 'Expert witness location', 'Deposition coordination support'],
              },
              {
                name: 'Court-Ordered Investigations',
                icon: <Users size={28} color="white" />,
                description: 'Location services for court-mandated legal matters',
                features: ['Heir and beneficiary searches', 'Estate and probate location', 'Guardian ad litem support', 'Legal party verification'],
              }
            ].map((service, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  {service.icon}
                </div>
                
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>
                  {service.name}
                </h3>
                
                <Link to="/quote?service=skip-tracing" data-testid={`quote-link-${index}`} style={{ display: 'inline-block', fontSize: '0.95rem', fontWeight: '600', color: 'var(--brand-primary)', marginBottom: '1rem', textDecoration: 'underline' }}>Request a Quote</Link>
                
                <p className="body-small" style={{
                  color: 'var(--text-secondary)',
                  marginBottom: '1.5rem'
                }}>
                  {service.description}
                </p>
                
                <ul style={{
                  textAlign: 'left',
                  paddingLeft: '0',
                  listStyle: 'none'
                }}>
                  {service.features.map((feature, fIndex) => (
                    <li key={fIndex} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: 'var(--text-secondary)'
                    }}>
                      <CheckCircle size={16} color="var(--brand-primary)" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Professional Skip Tracing */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Why Choose Professional Skip Tracing?
            </h2>
            
            <p className="body-medium" style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2rem',
              textAlign: 'center',
              lineHeight: '1.8'
            }}>
              Skip tracing in California for legal matters is a specialized practice that can be performed by licensed private investigators, registered process servers, or licensed collection agencies. Our professional services ensure legal compliance, efficiency, and confidentiality throughout the investigation process, exclusively serving attorneys, law firms, and legal professionals for court-related matters and service of process.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '2rem',
              marginTop: '3rem'
            }}>
              {[
                {
                  title: 'Licensed & Compliant',
                  description: 'California law allows skip tracing to be performed by licensed private investigators, registered process servers, or licensed collection agencies. We comply with FDCPA, GLBA, TCPA, and FCRA regulations, ensuring all investigations meet legal standards exclusively for court-related matters and service of process.'
                },
                {
                  title: 'Legal Matters Only',
                  description: 'We exclusively serve attorneys, law firms, and legal professionals for court-related matters. Our skip tracing services are designed to facilitate service of process, locate witnesses, and support legitimate legal proceedings in compliance with California law.'
                },
                {
                  title: 'Advanced Database Access',
                  description: 'Our investigators access proprietary databases unavailable to the public, including comprehensive public records, credit headers, utility records, and professional license searches for superior locate results in legal matters.'
                },
                {
                  title: 'Expert Investigation Skills',
                  description: 'With 85% overall success rates and 95% data accuracy, our experienced investigators use proven techniques including database searches, public records analysis, and field verification for service of process facilitation.'
                },
                {
                  title: 'Complete Confidentiality',
                  description: 'We maintain 100% confidentiality with secure data handling, encrypted communications, and discreet investigations that protect attorney-client privilege and maintain professional standards for all legal matters.'
                },
                {
                  title: 'Service of Process Support',
                  description: 'Our primary focus is facilitating service of process for legal professionals. Fast turnaround times (24-72 hours) ensure legal deadlines are met and proceedings can move forward without delay.'
                }
              ].map((benefit, index) => (
                <div key={index} style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)'
                }}>
                  <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                    {benefit.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Investigation Methods */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Our Investigation Methods
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Advanced techniques and databases for accurate and efficient results
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem'
          }}>
            {[
              'Public Records Database Search',
              'Social Media Investigation',
              'Employment History Research',
              'Property Records Analysis',
              'Credit Header Information',
              'Utility Company Records',
              'Motor Vehicle Records',
              'Professional License Searches',
              'Voter Registration Records',
              'Criminal Background Checks',
              'Civil Court Records',
              'Bankruptcy Filings Research'
            ].map((method, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                backgroundColor: 'var(--bg-page)',
                borderRadius: '8px',
                border: '1px solid var(--border-light)'
              }}>
                <MapPin size={20} color="var(--brand-primary)" />
                <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                  {method}
                </span>
              </div>
            ))}
          </div>

          <div style={{ maxWidth: '900px', margin: '3rem auto 0', textAlign: 'center' }}>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              Our multi-layered approach combines traditional investigation techniques with cutting-edge technology. We utilize comprehensive database searches to access public records, last known addresses, employment history, and affiliations. Social media monitoring tracks online activity for location clues, while network investigations explore known associates, family members, and social connections. For complex cases, field surveillance provides in-person observations to confirm information and locate hard-to-find individuals.
            </p>
          </div>
        </div>
      </section>

      {/* Legal Compliance */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>
              Legal Compliance & Ethics
            </h2>
            
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                California Licensing Requirements
              </h3>
              <p className="body-medium" style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                lineHeight: '1.8'
              }}>
                In California, skip tracing services can be performed by licensed private investigators, registered process servers, or licensed collection agencies. This requirement ensures that the practice adheres to legal standards and professional ethics. Our team consists of fully licensed California professionals with extensive experience in people location services for legal matters.
              </p>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                Federal Compliance Standards
              </h3>
              <p className="body-medium" style={{ 
                color: 'var(--text-secondary)', 
                marginBottom: '1.5rem',
                lineHeight: '1.8'
              }}>
                All skip tracing activities comply with federal laws including:
              </p>
              <ul style={{
                paddingLeft: '0',
                listStyle: 'none'
              }}>
                {[
                  'Fair Debt Collection Practices Act (FDCPA) - Governs debt collection communications and practices',
                  'Gramm-Leach-Bliley Act (GLBA) - Protects consumer financial information privacy',
                  'Telephone Consumer Protection Act (TCPA) - Regulates telephone communications and automated calls',
                  'Fair Credit Reporting Act (FCRA) - Governs use of consumer credit information'
                ].map((law, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    color: 'var(--text-secondary)'
                  }}>
                    <CheckCircle size={20} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span>{law}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Success Rates */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Success Rates & Timelines
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            textAlign: 'center'
          }}>
            {[
              { metric: '85%', label: 'Overall Success Rate', description: 'Successful location of subjects' },
              { metric: '24-72 Hours', label: 'Average Turnaround', description: 'For standard investigations' },
              { metric: '95%', label: 'Data Accuracy', description: 'Verified information quality' },
              { metric: '100%', label: 'Confidentiality', description: 'Secure and discreet service' }
            ].map((stat, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-page)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)'
              }}>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: 'var(--brand-primary)',
                  marginBottom: '0.5rem'
                }}>
                  {stat.metric}
                </div>
                <h3 className="heading-4" style={{ marginBottom: '0.5rem' }}>
                  {stat.label}
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  {stat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
              Related Legal Services
            </h3>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.8' }}>
              Skip tracing works hand-in-hand with process serving to ensure successful service of legal documents. Explore our complete range of services for California law firms.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/process-serving"
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: 'var(--brand-primary)',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Process Serving Services →
              </Link>
              <Link 
                to="/resources/legal-document-services-guide"
                style={{
                  padding: '1rem 2rem',
                  backgroundColor: 'transparent',
                  color: 'var(--brand-primary)',
                  border: '2px solid var(--brand-primary)',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ marginBottom: '3rem', textAlign: 'center' }}>
              Frequently Asked Questions
            </h2>

            {[
              {
                question: 'Is skip tracing legal in California?',
                answer: 'Yes, skip tracing is legal in California when performed by licensed private investigators, registered process servers, or licensed collection agencies who comply with federal and state regulations including the Fair Debt Collection Practices Act (FDCPA), Gramm-Leach-Bliley Act (GLBA), Telephone Consumer Protection Act (TCPA), and Fair Credit Reporting Act (FCRA). California law requires skip tracing to be performed by these licensed professionals to ensure adherence to legal standards and professional ethics.'
              },
              {
                question: 'How long does skip tracing take?',
                answer: 'Most skip tracing investigations are completed within 24-72 hours for standard cases. However, turnaround times can vary based on the complexity of the case, availability of information, and whether the subject is actively avoiding contact. Complex cases may require additional time for thorough investigation and verification.'
              },
              {
                question: 'What information do you need to start a skip trace?',
                answer: 'To begin a skip trace, we typically need the subject\'s full name, last known address, date of birth (if available), Social Security number (if available), and any additional information such as previous addresses, known associates, employment history, or phone numbers. The more information provided, the faster and more accurate the investigation.'
              },
              {
                question: 'What types of skip tracing services do you provide?',
                answer: 'We provide skip tracing services exclusively for legal matters. Our services are designed to facilitate service of process, locate witnesses for court proceedings, assist with heir and beneficiary searches in probate matters, and support other legitimate court-ordered legal needs. We serve attorneys, law firms, and legal professionals only, ensuring all investigations comply with California law and are conducted for authorized legal purposes.'
              },
              {
                question: 'Can you help with debt collection or personal searches?',
                answer: 'No, we only provide skip tracing services for legal matters. Our services are exclusively for attorneys, law firms, and legal professionals conducting authorized court-related proceedings. We specialize in facilitating service of process, locating witnesses, and supporting legitimate legal matters as required by California law. We do not provide skip tracing for debt collection, personal searches, or non-legal purposes.'
              },
              {
                question: 'What is your success rate for locating people?',
                answer: 'Our overall success rate for locating subjects is 85%, with 95% data accuracy for verified information. Success rates vary based on the availability of information, how long the person has been missing, and whether they are actively avoiding contact. Our team uses advanced databases, multiple investigation methods, and proprietary tools to maximize success rates.'
              },
              {
                question: 'Are skip tracing services confidential?',
                answer: 'Yes, we maintain 100% confidentiality in all skip tracing investigations. All information is handled discreetly, securely, and in compliance with privacy laws. Our investigators undergo background checks and sign confidentiality agreements. We employ secure communication channels and encrypted data storage to protect sensitive client information.'
              }
            ].map((faq, index) => (
              <div key={index} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                marginBottom: '1.5rem'
              }}>
                <h3 className="heading-4" style={{ marginBottom: '1rem', color: 'var(--brand-primary)' }}>
                  {faq.question}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        backgroundColor: 'var(--brand-primary)',
        padding: '80px 0',
        color: 'white'
      }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem', color: 'white' }}>
            Need Skip Tracing Services?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem', opacity: 0.9 }}>
            Professional skip tracing for attorneys and legal professionals - service of process and court-related matters
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link 
              to="/place-order"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'white',
                color: 'var(--brand-primary)',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Place Order Now
            </Link>
            <a href="/quote"
              style={{
                padding: '1rem 2rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Request a Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SkipTracingPage;
